'use client';

// Card thumbnail. Three tiers, in order of how truthful they are:
//   1. skins/profiles  — the real 3D model, from the saved texture.
//   2. banners/shields/fireworks — DECODED from the saved stateHash and drawn with the
//      owning tool's own renderer/color table, so the card shows the actual design.
//   3. everything else — a tinted gradient + the type's glyph.
//
// Tier 2 decoders are imported DYNAMICALLY: a grid with no banner in it must not pay for the
// banner renderer. Any failure (bad hash, a mask that would not load) falls back to tier 3
// rather than drawing something plausible-but-wrong — a thumbnail that quietly drops a
// pattern layer is worse than one that never claimed to show the design.
//
// Potions are still tier 3 on purpose: the auto color is a weighted average of EFFECT
// colors, which lives in the per-version game data, and pulling that into the library grid
// would put the ~1.95 MB corpus in this chunk.

import { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { TYPE_META, type LibraryItem, type LibraryType } from '@/app/saved/lib/library';

const SkinPreview3D = dynamic(() => import('@/app/saved/SkinPreview3D'), { ssr: false });

// One glyph per type. Paths are stroked with currentColor (set to the type hex by the wrapper).
const GLYPH: Record<LibraryType, React.ReactNode> = {
  favorites: <path strokeLinecap="round" strokeLinejoin="round" d="M11 3.8l2.1 4.7 5.1.5-3.8 3.4 1.1 5-4.5-2.6L6.6 17l1.1-5-3.8-3.4 5.1-.5z" />,
  recipes: <path strokeLinecap="round" strokeLinejoin="round" d="M4 5h16v14H4zM4 9h16M9 5v14" />,
  profile: <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />,
  skin: <path strokeLinecap="round" strokeLinejoin="round" d="M8 4h8v5H8zM6 9h12v7H6zM8 16v4M16 16v4" />,
  banner: <path strokeLinecap="round" strokeLinejoin="round" d="M6 3h12v14l-6-3-6 3zM6 3v0" />,
  shield: <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l7 3v6c0 4-3 7-7 9-4-2-7-5-7-9V6z" />,
  armor: <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6zM9 12l2 2 4-4" />,
  potion: <path strokeLinecap="round" strokeLinejoin="round" d="M10 3h4M11 3v4l-3 5a5 5 0 108 0l-3-5V3M8.5 14h7" />,
  firework: <path strokeLinecap="round" strokeLinejoin="round" d="M12 12V3M12 12l6-6M12 12l6 6M12 12H3M12 12l-5 5M12 21v-4" />,
  sign: <path strokeLinecap="round" strokeLinejoin="round" d="M4 5h16v9H4zM12 14v6M8 9h8M8 11h5" />,
  book: <path strokeLinecap="round" strokeLinejoin="round" d="M5 4h11a2 2 0 012 2v13H7a2 2 0 01-2-2zM5 17a2 2 0 012-2h11" />,
  armor_stand: <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v3M6 8h12M12 8v8M8 20h8" />,
  mob_builder: <path strokeLinecap="round" strokeLinejoin="round" d="M6 5h12v10H6zM9 9h.5M14.5 9h.5M9 20v-5M15 20v-5" />,
  enchantment: <path strokeLinecap="round" strokeLinejoin="round" d="M5 19l9-9M14 6l4 4M13 5l1 1M17 9l1 1M8 4l1 2 2 1-2 1-1 2-1-2-2-1 2-1z" />,
  command: <path strokeLinecap="round" strokeLinejoin="round" d="M7 8l4 4-4 4M13 16h4" />,
  execute: <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16v12H4zM7 10l3 2-3 2M13 14h4" />,
  tellraw: <path strokeLinecap="round" strokeLinejoin="round" d="M4 5h16v10H8l-4 4zM8 9h8M8 12h5" />,
  title: <path strokeLinecap="round" strokeLinejoin="round" d="M5 7V5h14v2M12 5v14M9 19h6" />,
  motd: <path strokeLinecap="round" strokeLinejoin="round" d="M4 5h16v11H7l-3 3zM8 10h8M8 13h4" />,
  color_code: <path strokeLinecap="round" strokeLinejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4M3 15h18" />,
  loot_table: <path strokeLinecap="round" strokeLinejoin="round" d="M4 8h16v11H4zM4 8l2-3h12l2 3M11 12h2v3h-2z" />,
  pixel_shape: <path strokeLinecap="round" strokeLinejoin="round" d="M9 4h6v3h3v6h-3v3H9v-3H6V7h3zM10 5v0" />,
};

type Size = 'grid' | 'list' | 'modal';

/** The tinted-gradient + glyph fallback. Every other tier falls back to this. */
function GlyphThumb({ type, size }: { type: LibraryType; size: Size }) {
  const hex = TYPE_META[type].hex;
  const px = size === 'list' ? 30 : size === 'modal' ? 54 : 42;
  return (
    <div
      className="w-full h-full flex items-center justify-center"
      style={{ background: `radial-gradient(120% 140% at 50% 0%, ${hex}22, #0f1311 70%)` }}
    >
      <svg width={px} height={px} viewBox="0 0 24 24" fill="none" stroke={hex} strokeWidth={1.6} style={{ opacity: 0.92 }}>
        {GLYPH[type]}
      </svg>
    </div>
  );
}

/** The real cloth, drawn by the banner editor's own compositor. */
function BannerThumb({ item, size }: { item: LibraryItem; size: Size }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const hash = typeof item.metadata.stateHash === 'string' ? item.metadata.stateHash : '';
  const shield = item.type === 'shield';

  useEffect(() => {
    if (!hash) { setFailed(true); return; }
    // Reset first: a card whose masks lost a race on the previous run must be able to
    // recover on a re-run, not sit on the glyph until it is unmounted.
    setFailed(false);
    let cancelled = false;
    (async () => {
      try {
        const [{ decodeFromHash }, { renderBannerThumbnail }] = await Promise.all([
          import('@/app/tools/banner-editor/lib/exporter'),
          import('@/app/tools/banner-editor/lib/renderer'),
        ]);
        const decoded = decodeFromHash(hash);
        if (!decoded) { if (!cancelled) setFailed(true); return; }
        const url = await renderBannerThumbnail({
          ...decoded,
          previewMode: shield ? 'shield' : 'banner',
          allowExtendedLayers: true,
        }, size === 'modal' ? 160 : 96);
        if (cancelled) return;
        if (url) setSrc(url); else setFailed(true);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [hash, shield, size]);

  if (failed) return <GlyphThumb type={item.type} size={size} />;
  return (
    <div
      className="w-full h-full flex items-center justify-center p-1.5"
      style={{ background: `radial-gradient(120% 140% at 50% 0%, ${TYPE_META[item.type].hex}22, #0f1311 70%)` }}
    >
      {/* eslint-disable-next-line @next/next/no-img-element -- a canvas data: URL, not a remote asset */}
      {src && <img src={src} alt="" className="max-w-full max-h-full object-contain" style={{ imageRendering: 'pixelated' }} />}
    </div>
  );
}

/** The first explosion, in the colors a firework STAR stores (not the dye's own). */
function FireworkThumb({ item, size }: { item: LibraryItem; size: Size }) {
  const [burst, setBurst] = useState<{ colors: string[]; fade: string[]; shape: string } | null>(null);
  const [failed, setFailed] = useState(false);
  const hash = typeof item.metadata.stateHash === 'string' ? item.metadata.stateHash : '';

  useEffect(() => {
    if (!hash) { setFailed(true); return; }
    setFailed(false);
    let cancelled = false;
    (async () => {
      try {
        const { decodeFromHash } = await import('@/app/tools/firework-editor/lib/exporter');
        const decoded = decodeFromHash(hash);
        const first = decoded?.explosions?.[0];
        if (cancelled) return;
        if (!first || first.colors.length === 0) { setFailed(true); return; }
        setBurst({ colors: first.colors, fade: first.fadeColors, shape: first.shape });
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [hash]);

  if (failed || !burst) return <GlyphThumb type={item.type} size={size} />;

  // One ray per color, evenly spaced — a legible stand-in for the shape, not a simulation.
  const rays = burst.shape === 'small_ball' ? 8 : burst.shape === 'creeper' ? 10 : 12;
  const px = size === 'list' ? 34 : size === 'modal' ? 76 : 52;
  const r = burst.shape === 'small_ball' ? 7.5 : 10;

  return (
    <div
      className="w-full h-full flex items-center justify-center"
      style={{ background: `radial-gradient(120% 140% at 50% 0%, ${TYPE_META[item.type].hex}22, #0f1311 70%)` }}
    >
      <svg width={px} height={px} viewBox="-12 -12 24 24">
        {Array.from({ length: rays }, (_, i) => {
          const a = (i / rays) * Math.PI * 2;
          const c = burst.colors[i % burst.colors.length];
          const f = burst.fade.length ? burst.fade[i % burst.fade.length] : null;
          const x = Math.cos(a) * r;
          const y = Math.sin(a) * r;
          return (
            <g key={i}>
              <line x1={0} y1={0} x2={x} y2={y} stroke={c} strokeWidth={1.5} strokeLinecap="round" />
              {f && <circle cx={x} cy={y} r={1.4} fill={f} />}
            </g>
          );
        })}
        <circle cx={0} cy={0} r={1.8} fill={burst.colors[0]} />
      </svg>
    </div>
  );
}

export function Thumbnail({ item, size = 'grid' }: { item: LibraryItem; size?: Size }) {
  const meta = item.metadata;

  // Real 3D for skins/profiles when a texture is present.
  const skinUrl = typeof meta.skinUrl === 'string' ? meta.skinUrl : undefined;
  const textureBase64 = typeof meta.textureBase64 === 'string' ? meta.textureBase64 : undefined;
  const previewSrc = textureBase64 || (skinUrl ? `/api/proxy-image?url=${encodeURIComponent(skinUrl)}` : '');
  if ((item.type === 'skin' || item.type === 'profile') && previewSrc) {
    const model = (meta.modelType === 'slim' ? 'slim' : 'classic') as 'classic' | 'slim';
    return (
      <div className="w-full h-full flex items-center justify-center bg-[radial-gradient(120%_140%_at_50%_0%,#1b211d,#0f1311)]">
        <SkinPreview3D src={previewSrc} modelType={model} size={size === 'modal' ? 150 : 120} className="rounded-md" />
      </div>
    );
  }

  if (item.type === 'banner' || item.type === 'shield') return <BannerThumb item={item} size={size} />;
  if (item.type === 'firework') return <FireworkThumb item={item} size={size} />;

  return <GlyphThumb type={item.type} size={size} />;
}
